import { Button, Col, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import {
  getTenantWorkflowById,
  getTenantWorkflows,
  getWorkflowLatestRevision,
} from '../../../Workflows/services/api-calls';

import DebugField from '../debug-field/DebugField';
import DynamicAttributeForm from '../../../../global/dynamic-attribute-form/DynamicAttributeForm';
import { Map as ImmutableMap } from 'immutable';
import LanguageSelect from '../../../../global/language-select/LanguageSelect';
import { RTS_TRANSLATION_INFO } from '../../services/constants';
import { TenantIdContext } from '../../../../components/app/App';
import { getRtsDebugResponsePromise } from '../../services/api-calls';
import { nanoid } from 'nanoid';
import spinner from '../../../../static/spinner.gif';
import { useLocation } from 'react-router-dom';
import Row from '@amzn/meridian/row';
import { AbSelectionField } from '../ab-selection-field/AbSelectionField';
import { WorkflowGraph } from '../../../Workflows/components/workflow-editor/workflow-graph';
import { DEFAULT_AB_BRANCH_NAME } from '../../../../global/constants';
import { saveSubworkflow } from '../../../Workflows/services/utils';

const ManualTextForm = ({ workflowId, sourceLocale, targetLocale, disableWorkflowSelection, live, simpleForm }) => {
  const [sourceText, setSourceText] = useState('');
  const [selectedSourceLocale, setSelectedSourceLocale] = useState('');
  const [selectedTargetLocale, setSelectedTargetLocale] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [workflowAttributes, setWorkflowAttributes] = useState(new ImmutableMap());
  const [tenantWorkflows, setTenantWorkflows] = useState([]);
  const [allowedSourceLocales, setAllowedSourceLocales] = useState(undefined);
  const [allowedTargetLocalesBySource, setAllowedTargetLocalesBySource] = useState(new Map());
  const tenantId = useContext(TenantIdContext);
  const [useLive, setUseLive] = useState(false);
  const [workflowData, setWorkflowData] = useState(null);
  const [selectedSubworkflow, setSelectedSubworkflow] = useState(null);
  const [workflowGraph, setWorkflowGraph] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [translationType, setTranslationType] = useState('CHAT');
  const [sessionId, setSessionId] = useState('');
  const [sentSessionId, setSentSessionId] = useState(undefined);
  const { search } = useLocation();

  useEffect(() => {
    if (workflowId) {
      setSelectedWorkflow(workflowId);
    }
  }, [workflowId]);

  useEffect(() => {
    setUseLive(live);
  }, [live]);

  const updateSelectedWorkflowGraph = useCallback(
    (source, target) => {
      if (!workflowData) {
        setSelectedBranch(null);
        return;
      }

      for (const sub of workflowData.subWorkflows) {
        if (sub.sourceLocale === source && sub.targetLocale === target) {
          const graph = new WorkflowGraph(sub);
          setSelectedSubworkflow(sub);
          setWorkflowGraph(graph);
          graph.hasAbTest ? setSelectedBranch(DEFAULT_AB_BRANCH_NAME) : setSelectedBranch(null);
          return;
        }
      }
      setWorkflowGraph(null);
      setSelectedBranch(null);
    },
    [workflowData]
  );

  const updateSourceLocale = useCallback(
    locale => {
      setSelectedSourceLocale(locale);
      updateSelectedWorkflowGraph(locale, selectedTargetLocale);
    },
    [updateSelectedWorkflowGraph, selectedTargetLocale]
  );

  const updateTargetLocale = useCallback(
    locale => {
      setSelectedTargetLocale(locale);
      updateSelectedWorkflowGraph(selectedSourceLocale, locale);
    },
    [updateSelectedWorkflowGraph, selectedSourceLocale]
  );

  useEffect(() => {
    setSelectedSourceLocale(sourceLocale);
  }, [sourceLocale]);

  useEffect(() => {
    setSelectedTargetLocale(targetLocale);
  }, [targetLocale]);

  useEffect(() => {
    updateSelectedWorkflowGraph(sourceLocale, targetLocale);
  }, [sourceLocale, targetLocale, updateSelectedWorkflowGraph]);

  const simulateTranslation = () => {
    if (
      !sourceText ||
      sourceText.length === 0 ||
      !selectedSourceLocale ||
      selectedSourceLocale.length === 0 ||
      !selectedTargetLocale ||
      selectedTargetLocale.length === 0
    ) {
      setError('Incorrect parameter(s), check and try again.');
      return;
    }

    if (selectedSourceLocale === selectedTargetLocale) {
      setError('Cannot translate from the language to itself.');
      return;
    }

    setIsLoading(true);
    setError('');

    const attributesToSend = {};
    workflowAttributes.valueSeq().forEach(attribute => (attributesToSend[attribute.name] = attribute.value));

    const resolvedSessionId = translationType === 'CHAT' ? (sessionId || crypto.randomUUID()) : undefined;

    getRtsDebugResponsePromise(
      tenantId,
      sourceText,
      selectedSourceLocale,
      selectedTargetLocale,
      attributesToSend,
      !useLive,
      selectedBranch,
      translationType,
      resolvedSessionId
    )
      .then(response => {
        const translationInfo = response.data[RTS_TRANSLATION_INFO];
        if (response.data.sourceDetectedLocale) {
          translationInfo.sourceDetectedLocale = response.data.sourceDetectedLocale;
        }
        setDebugData(translationInfo);
        setSentSessionId(resolvedSessionId);
        setIsLoading(false);
      })
      .catch(error => {
        setDebugData(null);
        setSentSessionId(null);
        setError(`Error while simulating the translation: ${error}`);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getTenantWorkflows(tenantId).then(response => {
      setTenantWorkflows(response.data);
    });
  }, [tenantId]);

  useEffect(() => {
    const params = new URLSearchParams(search);

    if (params.has('workflow')) {
      setSelectedWorkflow(params.get('workflow'));
    }

    if (params.has('selectedSourceLocale')) {
      updateSourceLocale(params.get('selectedSourceLocale'));
    }

    if (params.has('selectedTargetLocale')) {
      updateTargetLocale(params.get('selectedTargetLocale'));
    }
  }, [search, updateSourceLocale, updateTargetLocale]);

  const afterWorkflowDataLoaded = useCallback(response => {
    const selectorAttributes = response?.data?.selectorAttributes;
    if (selectorAttributes) {
      const remappedSelectorAttributes = new ImmutableMap(selectorAttributes).mapEntries(([key, value]) => [
        nanoid(),
        { name: key, value: value },
      ]);
      setWorkflowAttributes(remappedSelectorAttributes);
    }
    setWorkflowData(response?.data);
    const subworkflows = response?.data?.subWorkflows;
    if (subworkflows) {
      const allowedSourceLocaleSet = new Set();
      const allowedSourceToTargetLocalesMap = new Map();
      for (const subworkflow of subworkflows) {
        const sl = subworkflow.sourceLocale;
        const tl = subworkflow.targetLocale;
        allowedSourceLocaleSet.add(sl);
        if (!allowedSourceToTargetLocalesMap.has(sl)) {
          allowedSourceToTargetLocalesMap.set(sl, new Set());
        }
        allowedSourceToTargetLocalesMap.get(sl).add(tl);
      }
      setAllowedSourceLocales(allowedSourceLocaleSet);
      setAllowedTargetLocalesBySource(allowedSourceToTargetLocalesMap);
    }
  }, []);

  useEffect(() => {
    if (!selectedWorkflow) {
      setWorkflowAttributes(new ImmutableMap());
      setAllowedSourceLocales(undefined);
      setAllowedTargetLocalesBySource(new Map());
      return;
    }
    if (useLive) {
      getTenantWorkflowById(tenantId, selectedWorkflow).then(afterWorkflowDataLoaded);
    } else {
      getWorkflowLatestRevision(tenantId, selectedWorkflow).then(afterWorkflowDataLoaded);
    }
  }, [tenantId, selectedWorkflow, useLive, afterWorkflowDataLoaded]);

  const handleSelectedWorkflowChanged = useCallback(
    newWorkflow => {
      setSelectedWorkflow(newWorkflow);
      updateTargetLocale('');
      updateSourceLocale('');
      setSelectedBranch(DEFAULT_AB_BRANCH_NAME);
    },
    [updateSourceLocale, updateTargetLocale]
  );

  const handleSelectedBranchChanged = useCallback(newBranch => {
    setSelectedBranch(newBranch);
  }, []);

  const handleMarkTestSuccessful = useCallback(() => {
    if (workflowGraph.markTested(selectedBranch)) {
      saveSubworkflow(tenantId, workflowData.workflowId, selectedSubworkflow, workflowGraph, []);
    }
  }, [workflowGraph, selectedBranch, selectedSubworkflow, tenantId, workflowData]);

  return (
    <div>
      {error && <h5 className="color-red">{error}</h5>}
      {simpleForm ? null : (
        <h5>
          <b>Simulate an RTS translation</b>
        </h5>
      )}
      <Form autoComplete="false">
        {disableWorkflowSelection || simpleForm ? null : (
          <div>
            <Form.Label>Workflow selection</Form.Label>
            <Col>
              <Select width="40%" value={selectedWorkflow} onChange={handleSelectedWorkflowChanged}>
                <SelectOption key="" value="" label="[No workflow]" />
                {tenantWorkflows.map(workflow => (
                  <SelectOption key={workflow.workflowId} value={workflow.workflowId} label={workflow.workflowName} />
                ))}
              </Select>
            </Col>
          </div>
        )}
        {simpleForm ? null : (
          <Row className="m-3">
            <div>
              <input
                type="radio"
                id="useBuild"
                checked={!useLive}
                name="use_live"
                value="false"
                onChange={() => setUseLive(false)}
              />{' '}
              <label for="useBuild">Use Build Version</label>
            </div>
            <div className="pl-4">
              <input
                type="radio"
                id="useLive"
                checked={useLive}
                name="use_live"
                value="true"
                onChange={() => setUseLive(true)}
              />{' '}
              <label for="useLive">Use Live Version</label>
            </div>
          </Row>
        )}
        {simpleForm ? null : (
          <div>
            <Form.Label className="mt-2">Workflow selection attributes</Form.Label>
            <Col className="col-10">
              <DynamicAttributeForm
                value={workflowAttributes}
                onChange={newValue => {
                  setWorkflowAttributes(newValue);
                }}
                disabled={disableWorkflowSelection}
              />
            </Col>
          </div>
        )}
        <Form.Label>Source Text</Form.Label>
        <Col>
          <Form.Control
            placeholder="Some text to be translated"
            as="textarea"
            rows="6"
            value={sourceText}
            onChange={e => setSourceText(e.target.value)}
          />
        </Col>
        {simpleForm ? null : (
          <>
            <Form.Label className="mt-1">Source Language</Form.Label>
            <Col className="col-3">
              <LanguageSelect
                value={selectedSourceLocale}
                onChange={v => updateSourceLocale(v)}
                allowedLocales={allowedSourceLocales}
                className="w-100"
              />
            </Col>
            <Form.Label className="mt-1">Target Language</Form.Label>
            <Col className="col-3">
              <LanguageSelect
                value={selectedTargetLocale}
                onChange={v => updateTargetLocale(v)}
                allowedLocales={
                  selectedWorkflow && selectedWorkflow !== ''
                    ? selectedSourceLocale
                      ? allowedTargetLocalesBySource.get(selectedSourceLocale)
                      : []
                    : undefined
                }
                className="w-100"
              />
            </Col>
          </>
        )}
        <AbSelectionField
          workflowGraph={workflowGraph}
          onChange={handleSelectedBranchChanged}
          selectedBranch={selectedBranch}
          label="A/B Test Branch"
        />
        {simpleForm ? null : (
          <div className="mt-2">
            <Form.Label>Translation Type (LLM workflows will run for launched languages only)</Form.Label>
            <Col className="col-3">
              <Select value={translationType} onChange={setTranslationType}>
                <SelectOption value="CHAT" label="Luma" />
                <SelectOption value="OTHER" label="MTS" />
              </Select>
            </Col>
          </div>
        )}
        {!simpleForm && translationType === 'CHAT' && (
          <div className="mt-2">
            <Form.Label>
              Session Id (Contact ID){' '}
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Supply a session id to simulate a message for a contact or session. Reuse the same session id to
                    continue the conversation. Leave blank if you do not require session tracking.
                  </Tooltip>
                }
              >
                <span className="cursor-pointer">&#63;</span>
              </OverlayTrigger>
            </Form.Label>
            <Col className="col-5">
              <Form.Control
                placeholder=""
                value={sessionId}
                onChange={e => setSessionId(e.target.value)}
              />
            </Col>
          </div>
        )}
        <Button className="mt-2" variant="primary" size="sm" onClick={simulateTranslation}>
          Simulate
        </Button>
        {isLoading && <img src={spinner} alt="Loading..." className="App-spinner" />}
      </Form>
      {debugData && <DebugField data={debugData} sessionId={sentSessionId} />}
      {!useLive && debugData && (
        <Button className="mt-2" variant="primary" onClick={handleMarkTestSuccessful}>
          Mark Test Successful
        </Button>
      )}
    </div>
  );
};

export default ManualTextForm;
